import { SortDirection } from 'react-virtualized';
import dayjs from 'dayjs';
import { get, post } from '../../stores/_services/dataService';
import { observable, makeObservable } from 'mobx';

interface ITaskItemProgress {
    sortDate: string;
    displayDate: string;
    items: { text: string; createdOn: Date }[];
}

export interface ITaskItem {
    id: string;
    userTaskId: string;
    name: string;
    createdOn: Date | null;
    endedOn: Date | null;
    updatedOn: Date | null;
    statusSetOn: string | null;
    result: null | string;
    data: any;
    progressMap: unknown[] | null;
    progressRaw: string[];
    status: string;
    jobId: string;
    type: string;
    isRunning?: boolean;
    progress: { createdOn: Date | null; id: string; text: string }[];
}

class TaskItemStore {
    taskItem: ITaskItem = {
        id: '',
        userTaskId: '',
        name: '',
        createdOn: null,
        endedOn: null,
        updatedOn: null,
        statusSetOn: null,
        result: null,
        data: {},
        progressMap: null,
        progressRaw: [],
        status: '',
        jobId: '',
        type: '',
        progress: []
    };

    constructor(taskItem: ITaskItem) {
        makeObservable(this, {
            taskItem: observable
        });

        this._setTaskItemData(taskItem);
    }

    get elapsed(): number {
        const end = this.taskItem.endedOn ? dayjs(this.taskItem.endedOn) : dayjs();
        return dayjs(end).diff(this.taskItem.createdOn as Date);
    }

    update(taskItem: ITaskItem): void {
        this._setTaskItemData(taskItem);
    }

    async cancel(): Promise<void> {
        await post(`userTaskItems/${this.taskItem.id}/cancel`);
    }

    async getProgress(): Promise<void> {
        const options = {
            filter: {
                where: { taskItemId: this.taskItem.id },
                includeFields: ['id', 'text', 'createdOn'],
                order: `createdOn ${SortDirection.ASC}`,
                skip: this.taskItem.progressRaw.length
            }
        };

        const { data } = await get('userTaskItemProgresses', options);

        if (data.length > 0) {
            this._setProgress(data);
        }
    }

    _setProgress(data: any): void {
        const progressRaw = [...data.reverse(), ...this.taskItem.progressRaw];

        const progressObject: ITaskItemProgress[] = progressRaw.reduce((obj, item) => {
            const itemDate = dayjs(item.createdOn);
            const displayDate = itemDate.format('MMM D');
            const sortDate = itemDate.format('YYYYMMDD');

            obj[sortDate] = obj[sortDate] || {
                sortDate,
                displayDate,
                items: []
            };

            obj[sortDate].items.push(item);
            return obj;
        }, {});

        this.taskItem.progressMap = Object.values(progressObject).sort((a, b) => (a.sortDate > b.sortDate ? -1 : 1));
        this.taskItem.progressRaw = progressRaw;
    }

    _setTaskItemData(taskItem: ITaskItem): void {
        this.taskItem = {
            ...this.taskItem,
            ...taskItem,
            isRunning: taskItem.status !== 'cancelled' && taskItem.status !== 'failed' && taskItem.status !== 'completed'
        };

        if (!this.taskItem.progressMap && taskItem.progress) {
            // materialize id from index for cached progress array
            const progress = taskItem.progress.map(({ text, createdOn }, index: number) => ({ id: index + String(createdOn), text, createdOn }));
            this._setProgress(progress);
        }
    }
}

export default TaskItemStore;
